import React, { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useWidthCheck } from '@hooks';
import { MOBILE_WIDTH } from '@settings';
import { Markdown as MD } from '@features/markdown/components/Markdown';
import classNames from 'classnames';
import { useAppDispatch } from '@/store';
import { getItem } from '@/actions/pdp.actions';
import SizeAndCapacitySectionSelection from './SizeAndCapacitySectionSelection';

const SizeAndCapacity = ({ attributes, children = [], ...props }) => {
  const pdpItem = useSelector((state: any) => state.pdpItem);
  const dispatch = useAppDispatch();
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const { sizeId, size } = pdpItem?.details?.attributes || {};

    const newIdx = Math.max(
      children.findIndex(
        (c) => [sizeId, size].includes(c.attributes?.sizeId) || [sizeId, size].includes(c.attributes?.title)
      ),
      0
    );

    setActiveIdx(newIdx);
  }, [children, pdpItem]);

  const capacitySectionTitle = attributes?.title || 'Size & Capacity';

  const sectionSelectors = children.map((s) => {
    return {
      title: s.attributes.title,
    };
  });

  const handleActiveIdx = (idx: number) => {
    const sizeId = children[idx]?.attributes?.sizeId;
    const sizeTitle = children[idx]?.attributes?.title;
    const url = pdpItem?.variants?.sizes?.find(
      (s) => [s.value, s.id].includes(sizeId) || [s.value, s.id].includes(sizeTitle)
    )?.url;

    if (url) {
      dispatch(getItem(url));
      return;
    }

    setActiveIdx(idx);
  };

  return (
    <div id='ProductComparison' className='tw:bg-white tw:px-5 tw:py-10 tw:font-bold'>
      <h2 className='tw:mb-10 tw:text-center tw:text-2xl tw:text-brand-black'>{capacitySectionTitle}</h2>

      <div>
        {children.map((section, idx) => (
          <SizeAndCapacitySection key={idx} section={section.attributes} isActive={activeIdx === idx} />
        ))}

        {sectionSelectors?.length > 1 && (
          <SizeAndCapacitySectionSelection
            textOnly={attributes.textOnly}
            selectors={sectionSelectors}
            activeIdx={activeIdx}
            setActiveIdx={handleActiveIdx}
          />
        )}
      </div>
    </div>
  );
};

const SizeAndCapacitySection = ({ section, isActive }) => {
  const { image, description } = section;
  const isMobView = useWidthCheck(MOBILE_WIDTH);

  return (
    <div className={classNames('tw:mb-10 tw:animate-[fadeIn_0.5s] tw:last:mb-0', { 'tw:hidden': !isActive })}>
      <div
        className={classNames(
          'tw:mx-auto tw:mb-10 tw:flex tw:h-[440px] tw:max-w-[880px] tw:items-center tw:justify-center tw:md:h-[380px]'
        )}>
        <img
          src={isMobView && image.mobile?.url ? image.mobile?.url : image.desktop?.url}
          alt={image.alt}
          className='tw:max-h-[440px] tw:max-w-full tw:md:max-h-[380px]'
        />
      </div>
      {description && (
        <div className='tw:text-center tw:font-normal tw:text-[#303030]'>
          <MD source={description} />
        </div>
      )}
    </div>
  );
};

export default memo(SizeAndCapacity);
